import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, Renderer2, signal, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { takeUntil } from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeNode } from 'primeng/api';
import { ChipModule } from 'primeng/chip';

import { InputComponent } from '@src/app/standalone-components/input/input.component';
import { FilterControlComponent } from '@shared/components/vehicle-list/filter-control/filter-control.component';
import { IFilterVehicle, ISubmitFilters } from '@src/app/interfaces/filters-vehicle.interface';
import { FilterVehicleType } from '@src/app/enum/filters-vehicle.enum';
import { ICompanyCard, IGetVehicleTachographFileSchedule } from '@src/app/interfaces/api/files-ddd.interface';
import { TachographFilesStatusService } from './fasade-tachograph-files-status.service';
import { Destroyable } from '@src/app/utils/Destroyable';
import { Vehicle } from '@commons/models/domain/Vehicle';
import { TachographFilesListComponent } from '../components/tachograph-files-list/tachograph-files-list.component';
import { UserActivityAutoRefreshManager } from '../user-activity-auto-refresh.manager';
import { FilesDddService } from '@src/app/services/api/files-ddd.service';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';

const imports = [
  TranslateModule,
  CommonModule,
  InputComponent,
  FilterControlComponent,
  ButtonModule,
  FormsModule,
  TableModule,
  CheckboxModule,
  ReactiveFormsModule,
  SkeletonModule,
  DialogModule,
  TreeSelectModule,
  TachographFilesListComponent,
  ChipModule,
];

@Component({
  selector: 'app-tachograph-vehicles-files-status',
  standalone: true,
  imports,
  templateUrl: './tachograph-vehicles-files-status.component.html',
  styleUrls: ['./tachograph-vehicles-files-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TachographVehiclesFilesStatusComponent extends Destroyable implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('containerList') private containerListRef: ElementRef<HTMLDivElement>;

  @Output() public openFiles$: EventEmitter<string> = new EventEmitter();

  public readonly filesFasade = inject(TachographFilesStatusService);
  private readonly translate = inject(TranslateService);
  private readonly rendere2 = inject(Renderer2);
  private readonly filesDDDService = inject(FilesDddService);
  private readonly vehiclesService = inject(VehicleService);

  public filtersStatusConfig = signal<IFilterVehicle>(null!);
  public filtersStatusConfigCurrentValue = signal<ISubmitFilters<number>>(null!);
  public filtersCompanyConfig = signal<IFilterVehicle>(null!);
  public filtersCompanyConfigCurrentValue = signal<ISubmitFilters<number>>(null!);
  public searchValueControl: FormControl = new FormControl('');

  public selectedItems = signal<IGetVehicleTachographFileSchedule[]>([]);
  public isLoadingInit = signal(true);
  public isDeletingItems = signal(false);
  public cards = signal<ICompanyCard[]>([]);

  public isCheckedAll: boolean = false;
  public listHeight: string;

  public visibleForm: boolean;
  public vehiclesTreeList = signal<TreeNode<Vehicle>[]>([]);
  public selectedVehiclesForm: TreeNode<Vehicle>[] = [];

  private autoRefresherRef: UserActivityAutoRefreshManager;

  // constructor(private changeDetRef: ChangeDetectorRef) {
  //   super();
  // }

  ngOnInit(): void {
    this.autoRefresherRef = new UserActivityAutoRefreshManager(() => this.autoRefreshFilesStatus());
    this.autoRefresherRef.init();

    this.setFiltersConfig();
    this.filesDDDService.GetVehiclesWithTachographDisabled().subscribe({
      next: (data) => {
        this.setVehicleGroupList(data);
      }
    });
    this.getCompanyCards();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setListHeight();
    }, 0);
  }

  override ngOnDestroy(): void {
    this.destroy();
    this.autoRefresherRef.destroy();
  }

  public onGetSelectedItems(): void {
    // this.selectedItems().forEach((item) => {
    //   const el: HTMLButtonElement = document.querySelector(`[data-file="${item.VehicleId}"]`)!;
    //   el?.click();
    // });

    // this.onChangeValueCheckedAll(false);
  }

  public onChangeCompanyCard({ item, value }: any): void {
    if (value) {
      this.filesDDDService.AssignCompanyCardToVehicleSchedule({ CardId: value.Id, VehicleId: item.VehicleId }).subscribe();
      return;
    }
    this.filesDDDService.UnassignCompanyCardFromSchedule(item.VehicleId).subscribe();
  }

  private getCompanyCards(): void {
    this.filesDDDService.GetTachographCompanyCards().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.cards.set(res.Cards);
      }
    });
  }

  public deleteVehicles(): void {
    if (this.selectedItems().length) {
      this.isDeletingItems.set(true);
      const vehicleIds = this.selectedItems().map(item => item.VehicleId);

      this.filesFasade.SetTachographScheduleForVehicles(vehicleIds, false).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.isDeletingItems.set(false);
          this.isCheckedAll = false;
          this.onChangeValueCheckedAll(false);
          this.autoRefresherRef.refreshNow();
        }
      });
    }
  }

  public onAddVehicles(): void {
    this.visibleForm = false;
    const vehiclesIds = [...new Set(this.selectedVehiclesForm.map(item => item.children ? item.children.map(child => child.data!.Id) : item.data!.Id).flat())];

    if (vehiclesIds) {
      this.filesFasade.SetTachographScheduleForVehicles(vehiclesIds).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.selectedVehiclesForm = [];
          this.autoRefresherRef.refreshNow();
        }
      });
    }
  }

  private setVehicleGroupList(vehicles: Vehicle[]): void {
    const {list} = this.vehiclesService.GetVehiclesGrouped(vehicles);
    this.vehiclesTreeList.set(list);
  }

  private setFiltersConfig(): void {
    // this.filtersStatusConfig.set({
    //   filterLabel: this.translate.instant('Status'),
    //   type: FilterVehicleType.Group,
    //   dropdownLabel: this.translate.instant('Status pobierania'),
    //   options: [
    //     { label: 'Błąd pobierania - słaby zasięg', value: 1,  },
    //     { label: 'Trwa pobieranie danych', value: 2,  },
    //     { label: 'Zaplanowane', value: 3,  },
    //     { label: 'Pobrano', value: 4,  },
    //   ],
    //   isVisibleSelectedOptionLabel: true,
    // });
    // this.filtersCompanyConfig.set({
    //   filterLabel: this.translate.instant('Firma'),
    //   type: FilterVehicleType.Group,
    //   dropdownLabel: this.translate.instant('Wybierz firmę'),
    //   options: [
    //     { label: 'Tekom', value: 1,  },
    //     { label: 'Apple', value: 2,  },
    //     { label: 'Microsoft', value: 3,  },
    //   ],
    //   isVisibleSelectedOptionLabel: true,
    // });
  }

  public onChangeDownloadMode({ value, item }: any): void {
    this.filesFasade.SetTachographScheduleForVehicles([item.VehicleId], value).pipe(takeUntil(this.destroy$)).subscribe();
  }

  public forceFile(data: { button: HTMLButtonElement, item: IGetVehicleTachographFileSchedule }): void {
    this.rendere2.setProperty(data.button, 'disabled', true);

    this.filesFasade.ForceVehicleFile(data.item.VehicleId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        // this.rendere2.setProperty(button, 'disabled', false);
      }
    });
  }

  private autoRefreshFilesStatus(): void {
    this.filesFasade.GetVehicleTachographFileSchedule(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoadingInit.set(false);
        }
      });
  }

  public onChangeStatusFilters(data: ISubmitFilters): void {
    this.filtersStatusConfigCurrentValue.set(data);
  }

  public onChangeCompanyFilters(data: ISubmitFilters): void {
    this.filtersCompanyConfigCurrentValue.set(data);
  }

  public addVehicle(): void {
    this.visibleForm = true;
  }

  public onChangeValueCheckedAll(value: boolean): void {
    this.selectedItems.set(value ? [...this.filesFasade.vehicleList()] : []);

    if (!value) {
      // this.uncheckAllPCheckboxInputs();
      // this.changeDetRef.detectChanges();
    }
  }

  // private uncheckAllPCheckboxInputs(): void {
  //   const checkboxes = Array.from(document.getElementsByClassName('p-checkbox-input'));
  //   console.log(checkboxes);
  //   checkboxes.forEach((cb) => {
  //     (cb as HTMLInputElement).checked = false;
  //   });
  // }

  public onCheckedItem(list: IGetVehicleTachographFileSchedule[]): void {
    this.selectedItems.set(list);
  }

  private setListHeight(): void {
    this.listHeight = (this.containerListRef.nativeElement.clientHeight - 45) + 'px';
    this.rendere2.setStyle(document.getElementsByClassName('p-datatable-table-container').item(0), 'max-height', this.listHeight);
    this.rendere2.setStyle(document.getElementsByClassName('p-datatable-table-container').item(0), 'height', this.listHeight);
  }

}
